"use client";

import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { PhoneCall, MapPin, Clock, ShieldCheck, CircleQuestionMark as HelpCircle, ArrowRight } from 'lucide-react';
interface FaqItem {
  id: string;
  question: string;
  answer: string;
  badgeText?: string;
}
const faqList: FaqItem[] = [{
  id: "item-1",
  question: "What areas in and around Jonesboro do you serve for 24/7 emergencies?",
  answer: "We provide rapid emergency plumbing dispatch throughout Jonesboro and surrounding Clayton County communities including Morrow, Riverdale, Forest Park, Stockbridge, Ellenwood, and Lovejoy. Our local service trucks are staged to reach your home or business swiftly.",
  badgeText: "Coverage"
}, {
  id: "item-2",
  question: "How quickly can an emergency plumber arrive at my property?",
  answer: "Our average response time for urgent calls in the Jonesboro area is 30 to 60 minutes. When you call our 24/7 emergency line, our dispatch team immediately routes the nearest licensed plumber with fully equipped service vehicles.",
  badgeText: "Rapid Dispatch"
}, {
  id: "item-3",
  question: "What plumbing issues qualify as an immediate emergency?",
  answer: "Urgent emergencies include burst or leaking water supply pipes, severe sewer backups, overflowing toilets, non-functioning main shutoff valves, gas water heater leaks, and complete loss of running water. If unaddressed water poses property damage or health risks, treat it as an emergency.",
  badgeText: "Emergencies"
}, {
  id: "item-4",
  question: "Do you charge extra fees for nights, weekends, or holidays?",
  answer: "We offer transparent, upfront pricing with clear quotes before work starts. We explain any emergency dispatch fees clearly over the phone before sending a technician, so there are never hidden surprises on your invoice.",
  badgeText: "Transparent Pricing"
}, {
  id: "item-5",
  question: "Can your technicians fix standard water heaters and tankless systems on-site?",
  answer: "Yes. Our master plumbers carry replacement parts, heating elements, valves, and diagnostic tools for all major residential and commercial brands including Rheem, Rinnai, A.O. Smith, Navien, and Bradford White.",
  badgeText: "Water Heaters"
}, {
  id: "item-6",
  question: "What steps should I take while waiting for the plumber to arrive?",
  answer: "First, locate and turn off your home's main water shutoff valve if water is actively leaking. If the issue is electrical near water or a water heater, switch off the breaker. Avoid using drains, clear paths to the problem area, and keep family members away from standing water.",
  badgeText: "Safety Advice"
}, {
  id: "item-7",
  question: "Are your plumbers licensed, insured, and background-checked?",
  answer: "Every plumber dispatched by Jonesboro Emergency Plumber is state-licensed, thoroughly insured, and vetted. We follow strict local Georgia building codes and back our repairs with full labor and parts warranties.",
  badgeText: "Certified"
}];
export default function ServiceAreaFaq() {
  return <section id="serviceareafaq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <Badge variant="outline" className="mb-3 border-accent text-accent-foreground font-semibold px-3 py-1 gap-1.5">
            <HelpCircle className="size-3.5 text-accent" />
            <span>Frequently Asked Questions</span>
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl font-bold tracking-tight text-primary mb-4">
            Jonesboro Service Coverage &amp; Dispatch FAQs
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Get straightforward answers about our emergency response times,
            coverage zones, and on-site repair procedures across Clayton County.
          </p>
        </div>

        {/* FAQ Accordion List */}
        <div className="bg-card border border-border rounded-xl shadow-sm p-4 sm:p-6 md:p-8 mb-10">
          <Accordion type="single" collapsible className="w-full divide-y divide-border">
            {faqList.map((faq, index) => <AccordionItem key={faq.id} value={faq.id} data-index={index} className="py-2 border-b-0">
                <AccordionTrigger className="text-left font-default text-base sm:text-lg font-semibold text-primary hover:no-underline hover:text-primary/80 group py-4 transition-colors">
                  <div className="flex flex-wrap items-center gap-2 pr-4">
                    <span>{faq.question}</span>
                    {faq.badgeText && <span className="inline-block text-xs font-normal text-muted-foreground bg-secondary px-2 py-0.5 rounded-md">
                        {faq.badgeText}
                      </span>}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-4 pr-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {/* Bottom Contact Reassurance Bar */}
        <div className="bg-secondary/60 border border-border rounded-xl p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="bg-primary text-primary-foreground p-3 rounded-lg shrink-0 mt-1">
              <Clock className="size-6 text-accent" />
            </div>
            <div>
              <h3 className="font-default text-lg font-bold text-primary mb-1">
                Have a plumbing emergency right now?
              </h3>
              <p className="text-sm text-muted-foreground">
                Our Jonesboro dispatch team is standing by 24 hours a day, 7 days a week.
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <MapPin className="size-3.5 text-accent" /> Jonesboro &amp; Nearby
                </span>
                <span className="flex items-center gap-1">
                  <ShieldCheck className="size-3.5 text-accent" /> Licensed &amp; Insured
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild variant="default" size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-semibold">
              <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                <PhoneCall className="size-4 text-accent" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border text-foreground hover:bg-background/80">
              <Link to="/contact/" className="flex items-center justify-center gap-1.5">
                <span>Request Service</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}